import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
} from "react-router";
import type { Route } from "./+types/root";

import "./styles/tailwind.css";

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="dark">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content="noindex, nofollow" />
        <title>Revenue Boost Admin</title>
        <Meta />
        <Links />
      </head>
      <body className="min-h-screen bg-background text-foreground antialiased">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let status = 500;
  let message = "Something went wrong";
  let details = "An unexpected error occurred. Please try again.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    status = error.status;
    switch (status) {
      case 404:
        message = "Page not found";
        details = "The page you're looking for doesn't exist or has been moved.";
        break;
      case 401:
        message = "Unauthorized";
        details = "Please log in to access this page.";
        break;
      case 403:
        message = "Access denied";
        details = "You don't have permission to access this resource.";
        break;
      default:
        message = `Error ${status}`;
        details = error.statusText || details;
    }
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  const emoji = status === 404 ? "🔍" : status === 401 ? "🔒" : status === 403 ? "🚫" : "⚠️";

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-zinc-900">
      <div className="max-w-md text-center">
        <div className="text-6xl mb-4">{emoji}</div>
        <h1 className="text-3xl font-bold text-zinc-100 mb-2">{message}</h1>
        <p className="text-zinc-400 mb-6">{details}</p>
        <div className="flex gap-3 justify-center">
          <a
            href="/"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Go to Dashboard
          </a>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-100 rounded-lg transition-colors"
          >
            Go Back
          </button>
        </div>
        {stack && (
          <pre className="mt-6 overflow-auto rounded-lg bg-zinc-800 p-4 text-left text-xs text-zinc-400 max-h-48">
            {stack}
          </pre>
        )}
      </div>
    </div>
  );
}

